import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PlansSubTable from "../../sections/subscriptionDashboard/PlansSubTable";
import PlanHeader from "../../sections/subscriptionDashboard/PlanHeader";
import EditPlan from "../../sections/subscriptionDashboard/EditPlan";
import {
  useCreatePlan,
  usePlans,
  useUpdatePlan,
} from "../../hooks/useSubscribtionDashboard";

export default function PlansManagement() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // pagination state (TablePagination is zero-based for page index)
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10); // rows per page

  const offset = page * limit;

  const {
    data: plansData,
    isLoading: plansLoading,
    isError: plansError,
    error: plansErrorObj,
    refetch: plansRefetch,
  } = usePlans({
    limit,
    offset,
  });
  const { mutate, isPending } = useCreatePlan();
  const { mutate: updateMutate, isPending: isPendingUpdate } = useUpdatePlan();

  const getResolvedAnnualPrice = (values) => {
    const monthlyPrice = Number(values?.price) || 0;
    const hasAnnualPrice =
      values?.annualPrice !== "" &&
      values?.annualPrice !== null &&
      values?.annualPrice !== undefined;

    return hasAnnualPrice ? Number(values.annualPrice) : monthlyPrice * 11;
  };

  const [formData, setFormData] = useState({
    name: "",
    mode: "Free",
    paystackId: "",
    annualPlanId: "",
    description: "",
    durationInDays: 7,
    initialSetupFee: 0,
    price: 0,
    annualPrice: "",
    color: "1E61DC",

    gracePeriodDays: 7,
    trialDurationDays: 7,
    earnToPayMethod: "FlatRate",
    earnToPayAmount: 100,
    features: [],
    privileges: [],
    fees: [],
    isActive: true,
  });

  useEffect(() => {
    if (selectedPlan) {
      setFormData({
        name: selectedPlan.name || "",
        mode: selectedPlan.mode || "Free",
        paystackId: selectedPlan.paystackId || "",
        annualPlanId: selectedPlan.annualPlanId || "",
        description: selectedPlan.description || "",
        durationInDays: selectedPlan.durationInDays || 7,
        initialSetupFee: selectedPlan.initialSetupFee || 0,
        price: selectedPlan.price || 0,
        annualPrice: selectedPlan.annualPrice ?? "",
        color: selectedPlan.color || "1E61DC",
        gracePeriodDays: selectedPlan.gracePeriodDays || 7,
        trialDurationDays: selectedPlan.trialDurationDays || 7,
        earnToPayMethod: selectedPlan.earnToPayMethod || "FlatRate",
        earnToPayAmount: selectedPlan.earnToPayAmount || 100,
        features: Array.isArray(selectedPlan.features)
          ? selectedPlan.features
          : [],
        privileges: Array.isArray(selectedPlan.privileges)
          ? selectedPlan.privileges
          : [],
        fees: Array.isArray(selectedPlan.fees) ? selectedPlan.fees : [],
        isActive:
          selectedPlan.isActive !== undefined ? selectedPlan.isActive : true,
      });
    }
  }, [selectedPlan]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreatePlan = () => {
    setEditMode(false);
    setSelectedPlan(null);
    setFormData({
      name: "",
      mode: "Free",
      paystackId: "",
      annualPlanId: "",
      description: "",
      durationInDays: 7,
      initialSetupFee: 0,
      price: 0,
      annualPrice: "",
      color: "1E61DC",
      gracePeriodDays: 7,
      trialDurationDays: 7,
      earnToPayMethod: "FlatRate",
      earnToPayAmount: 100,
      features: [],
      privileges: [],

      fees: [],

      isActive: true,
    });
    setDrawerOpen(true);
    handleMenuClose();
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setShowConfirmation(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleProceedToConfirmation = () => {
    setShowConfirmation(true);
  };

  const handleBackToForm = () => {
    setShowConfirmation(false);
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    if (selectedPlan) {
      updateMutate(
        {
          ...formData,
          durationInDays: Number(formData?.durationInDays),
          initialSetupFee: Number(formData?.initialSetupFee),
          price: Number(formData?.price),
          annualPrice: getResolvedAnnualPrice(formData),
          id: selectedPlan?.id,
        },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            setShowConfirmation(false);
          },
        },
      );
    } else {
      mutate(
        {
          ...formData,
          durationInDays: Number(formData?.durationInDays),
          initialSetupFee: Number(formData?.initialSetupFee),
          price: Number(formData?.price),
          annualPrice: getResolvedAnnualPrice(formData),
        },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            setShowConfirmation(false);
          },
        },
      );
    }
  };

  return (
    <Box>
      <PlanHeader handleCreatePlan={handleCreatePlan} />
      <PlansSubTable
        plans={plansData?.data?.data}
        plansLoading={plansLoading}
        plansError={plansError}
        plansErrorObj={plansErrorObj}
        setEditMode={setEditMode}
        setDrawerOpen={setDrawerOpen}
        setSelectedPlan={setSelectedPlan}
        plansRefetch={plansRefetch}
        page={page}
        limit={limit}
        totalCount={plansData?.data?.meta?.total ?? 0}
        onPageChange={(newPage) => setPage(newPage)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(0);
        }}
        onEditTeamPricing={(plan) => {
          navigate("/subscritionDashboard/team-pricing", {
            state: {
              planId: plan.id ?? plan._id,
              plan,
            },
          });
        }}
      />
      <EditPlan
        drawerOpen={drawerOpen}
        handleCloseDrawer={handleCloseDrawer}
        selectedPlan={selectedPlan}
        editMode={editMode}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleProceedToConfirmation={handleProceedToConfirmation}
        showConfirmation={showConfirmation}
        handleBackToForm={handleBackToForm}
        handleFinalSubmit={handleFinalSubmit}
        isPendingUpdate={isPendingUpdate}
        isPending={isPending}
      />
    </Box>
  );
}
